import {
  useContext,
  useEffect,
  createContext,
  ReactNode,
  useCallback,
  useMemo,
  useState,
} from "react";
import { gql, useMutation, useLazyQuery } from "@apollo/client";
import { useHistory } from "react-router";
import { sanitizeUser } from "./utils";
import { useStoreContext } from "../../contexts/StoreProvider";

const LOGIN = gql`
  mutation Login($input: UsersPermissionsLoginInput!) {
    login(input: $input) {
      jwt
      user {
        id
        email
        first_name
        last_name
        adp_auth_num
        phone
        role {
          id
          name
          description
          type
        }
        password_updated_at
        mfa_enabled
      }
    }
  }
`;

const CURRENT_USER = gql`
  query CurrentUser {
    me {
      first_name
      last_name
      adp_auth_num
      phone
      locations {
        id
        name
      }
      email
      id
      username
      role {
        id
        description
        type
        name
      }
      password_updated_at
      mfa_enabled
    }
  }
`;

const LOGOUT = gql`
  mutation Logout {
    logout {
      message
    }
  }
`;

type AuthContextType = {
  login: (
    identifier: string,
    password: string,
    redirectTo?: string
  ) => Promise<void>;
  logout: (redirectTo?: string) => Promise<void>;
  timeout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);
export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuthContext must be used within a AuthProvider");
  }
  return context;
};
export const AuthProvider = (props: { children: ReactNode }) => {
  const { dispatch } = useStoreContext();
  const history = useHistory();
  const [tabId] = useState(Math.random().toString(36).substr(2, 9));
  const [, setBoundaryError] = useState<Error | null>(null);

  const setLoading = useCallback(
    (loading: boolean) => {
      dispatch({
        type: "LOADING",
        payload: loading,
      });
    },
    [dispatch]
  );

  const [authenticate, { error: authError }] = useMutation(LOGIN, {
    fetchPolicy: "no-cache",
    onError: () => {
      setLoading(false);
      throw new Error("Error authenticating user.");
    },
    onCompleted: (data) => {
      dispatch({
        type: "UPDATE_USER",
        payload: sanitizeUser(data.login.user),
      });
    },
  });

  const [getUser, { loading: getUserLoading }] = useLazyQuery(CURRENT_USER, {
    fetchPolicy: "no-cache",
    errorPolicy: "ignore",
    onCompleted: (data) => {
      if (data.me) {
        dispatch({
          type: "UPDATE_USER",
          payload: sanitizeUser(data.me),
        });
      }
    },
    onError: (error) => {
      // Ignore "Forbidden access" — just means user is not logged in
      if (error.message === "Forbidden access") return;
      setBoundaryError(() => {
        throw error;
      });
    },
  });

  const [deauthenticate, { client }] = useMutation(LOGOUT, {
    fetchPolicy: "no-cache",
    onError: (error) => {
      if (error.graphQLErrors?.[0]?.message !== "Forbidden") {
        console.error("Logout error:", error);
      }
      // Always log out the user locally regardless of server-side error
      dispatch({
        type: "LOGOUT_USER",
      });
    },
    onCompleted: () => {
      dispatch({
        type: "LOGOUT_USER",
      });
    },
  });

  useEffect(() => {
    getUser();
  }, [getUser]);

  useEffect(() => {
    setLoading(getUserLoading);
  }, [getUserLoading, setLoading]);

  const login = async (
    identifier: string,
    password: string,
    redirectTo?: string
  ) => {
    setLoading(true);
    await authenticate({
      variables: {
        input: {
          identifier,
          password,
          provider: "local",
        },
      },
    });

    setLoading(false);
    if (redirectTo && !authError) {
      history.push(redirectTo);
    }
  };

  // Broadcast logout events in other tabs
  const allTabsFunctions = useMemo(() => {
    const logout = async (redirectTo = "/logout") => {
      setLoading(true);
      await deauthenticate();
      await client.clearStore();
      setLoading(false);
      history.replace(redirectTo);
    };

    const passwordExpired = async () => {
      setLoading(true);
      await deauthenticate();
      await client.clearStore();
      setLoading(false);
    };

    return {
      logout,
      timeout: async () => {
        logout("/timeout");
      },
      passwordExpired,
    };
  }, [client, deauthenticate, history, setLoading]);

  const executeAllTabs = async (functionName: string) => {
    if (typeof BroadcastChannel === "function") {
      const bc = new BroadcastChannel("authFunctions");
      bc.postMessage({ functionName, tabId });
    }
    return allTabsFunctions[functionName]();
  };

  // Respond to logout events in other tabs
  useEffect(() => {
    if (typeof BroadcastChannel === "function") {
      const bc = new BroadcastChannel("authFunctions");
      bc.onmessage = (event) => {
        const { functionName, tabId: eventTabId } = event.data;
        // Don't fire events that originated from this tab
        if (tabId !== eventTabId) {
          allTabsFunctions[functionName]();
        }
      };
      return () => {
        bc.close();
      };
    }
    // This is just here to make TS happy
    return () => undefined;
  }, [allTabsFunctions, tabId]);

  const logout = async () => executeAllTabs("logout");
  const timeout = async () => executeAllTabs("timeout");

  return (
    <AuthContext.Provider value={{ login, logout, timeout }}>
      {props.children}
    </AuthContext.Provider>
  );
};
